// pages/Dashboard/Produtos.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Card, CardContent, Grid, Divider,
  MenuItem, TextField, Chip, Stack, LinearProgress, Avatar,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { useTheme, alpha, lighten, darken } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation } from "react-query";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { getDashboardMetricInsights } from "../api/client";

import { useDashboard } from "../hooks/useDashboard";
import { useCategories } from "../hooks/useCategories";

/* ===== Helpers de paleta por tema ===== */
const buildSeries = (p) => [
  p.primary.main,
  (p.secondary?.main ?? p.info.main),
  p.warning.main,
  p.success.main,
  p.error.main,
  p.info.main,
  darken(p.primary.main, 0.2),
  lighten(p.primary.main, 0.35),
];

const BRL = (v)=> Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});

const LegendList = ({ items = [], getColor, maxHeight = 84 }) => (
  <Box
    sx={{
      mt: 1,
      maxHeight,
      overflow: "auto",
      pr: 0.5,
    }}
  >
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {items.map((it, i) => (
        <Stack
          key={`${it?.name ?? "item"}-${i}`}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ minWidth: 120 }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: getColor(i, it),
              flex: "0 0 auto",
            }}
          />
          <Typography variant="caption" sx={{ lineHeight: 1.2 }} noWrap title={it?.name ?? ""}>
            {it?.name ?? "-"}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);

const KpiCard = ({ icon, title, value, sub, color }) => (
  <Card sx={{ height:"100%" }}>
    <CardContent sx={{ display:"flex", alignItems:"center", gap:2 }}>
      <Avatar sx={{ bgcolor:color, width:44, height:44 }}>{icon}</Avatar>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
        <Typography variant="h4" sx={{ color }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

const Produtos = ({ start, end, idsProjetos }) => {
  const theme = useTheme();
  const p = theme.palette;
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const SERIES = useMemo(()=>buildSeries(p), [p]);

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null);
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);
  const lastCfgRef = React.useRef(null);

  const metricInsightsMutation = useMutation(
    async ({ metric, metricName, metricDefinition, metricValue, metricHuman }) => {
      const payload = {
        metric,
        metricName,
        metricDefinition,
        metricValue,
        metricHuman,
        period: { start, end },
        summaryContext: {
          totalRevenue: cards.totalRevenue ?? null,
          totalSalesUnits: cards.totalSalesUnits ?? null,
          avgConvPct: cards.avgConvPct ?? null,
          promoPct: cards.promoPct ?? null,
        },
        filters: {
          availability: availabilityFilter,
          id_categoria: idCategoria || null,
          id_subcategoria: idSubcategoria || null,
          ids_projetos: idsProjetos,
        },
      };
      return getDashboardMetricInsights("produtos", payload);
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    lastCfgRef.current = cfg;
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => (
    <Tooltip title="Explicar com IA">
      <IconButton size="small" onClick={() => openAi(cfg)} aria-label="Explicar com IA">
        <AutoAwesomeIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );

  // Filtros locais
  const [idCategoria, setIdCategoria] = useState("");
  const [idSubcategoria, setIdSubcategoria] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");

  const { rootCategories = [], isLoading: categoriesLoading } = useCategories({
    page: 1,
    limit: 500,
  });

  const categoryNodeById = useMemo(() => {
    const m = new Map();
    (rootCategories || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [rootCategories]);

  const subcategoryOptions = useMemo(() => {
    if (!idCategoria) return [];
    const node = categoryNodeById.get(String(idCategoria));
    return (node?.children || []).map((s) => ({ value: String(s.id), label: s.nome }));
  }, [idCategoria, categoryNodeById]);

  useEffect(() => {
    if (!idSubcategoria) return;
    const ok = subcategoryOptions.some((o) => o.value === String(idSubcategoria));
    if (!ok) setIdSubcategoria("");
  }, [idCategoria, idSubcategoria, subcategoryOptions]);

  const categoryQueryFilter = useMemo(() => {
    if (idSubcategoria) return { ids_subcategorias: [Number(idSubcategoria)] };
    if (idCategoria) return { ids_categorias: [Number(idCategoria)] };
    return {};
  }, [idCategoria, idSubcategoria]);

  useEffect(() => {
    setPage(1);
  }, [idCategoria, idSubcategoria]);

  // ====== CHARTS ======
  const chartsParams = useMemo(() => {
    const params = { start, end, ...categoryQueryFilter };
    if (availabilityFilter !== "all") params.availability = availabilityFilter;
    if (idsProjetos) params.ids_projetos = idsProjetos;
    return params;
  }, [start, end, availabilityFilter, idsProjetos, categoryQueryFilter]);

  const {
    data: chartsResp,
    isLoading: chartsLoading,
    error: chartsError,
  } = useDashboard("produtos", "charts", chartsParams, {
    select: (resp) => resp?.data || resp || {},
  });

  const cards = chartsResp?.cards || {};
  const charts = chartsResp?.charts || {};

  // ====== TABLE ======
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tableParams = useMemo(() => {
    const params = { start: start, end: end, page, limit: pageSize, ...categoryQueryFilter };
    if (availabilityFilter !== "all") params.availability = availabilityFilter;
    if (idsProjetos) params.ids_projetos = idsProjetos;
    return params;
  }, [start, end, page, pageSize, availabilityFilter, idsProjetos, categoryQueryFilter]);

  const {
    data: tableResp,
    isLoading: tableLoading,
  } = useDashboard("produtos", "table", tableParams, {
    keepPreviousData: true,
    select: (resp) => ({
      rows: resp?.data || [],
      pagination: resp?.pagination || { total: 0, page, limit: pageSize, pages: 0 },
    }),
  });

  const rowsGrid = tableResp?.rows || [];
  const totalRows = tableResp?.pagination?.total || 0;

  const columnsGrid = [
    { field: "product", headerName: "Produto", flex: 1, minWidth: 280 },
    { field: "category", headerName: "Categoria", width: 200 },
    { field: "subcategory", headerName: "Subcategoria", width: 220 },
    {
      field: "priceSale",
      headerName: "Preço Aplicado",
      width: 150,
      valueFormatter: ({ value }) => BRL(value),
    },
    { field: "views", headerName: "Procuras (views)", width: 150 },
    { field: "sales", headerName: "Vendas (un)", width: 130 },
    {
      field: "conversionPct",
      headerName: "Conversão",
      width: 130,
      valueFormatter: ({ value }) => `${Number(value||0).toFixed(1)}%`,
      sortComparator: (a, b) => Number(a) - Number(b),
    },
    {
      field: "revenue",
      headerName: "Receita (R$)",
      width: 150,
      valueFormatter: ({ value }) => BRL(value),
    },
    {
      field: "ticketUnit",
      headerName: "Ticket Médio",
      width: 150,
      valueFormatter: ({ value }) => BRL(value),
    },
    {
      field: "availability",
      headerName: "Disponibilidade",
      width: 160,
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.value === "imediata" ? "success" : "warning"}
          label={params.value === "imediata" ? "Imediata" : "Sob encomenda"}
        />
      ),
    },
  ];

  const showSubcategoryFilter =
    !categoriesLoading && Boolean(idCategoria) && subcategoryOptions.length > 0;

  return (
    <Box>
      {/* ===== LINHA 1: FILTROS ===== */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={showSubcategoryFilter ? 4 : 6}>
          <Card><CardContent>
            <TextField
              select
              fullWidth
              size="small"
              label="Filtrar por categoria"
              value={idCategoria}
              disabled={categoriesLoading}
              onChange={(e) => {
                setIdCategoria(e.target.value);
                setIdSubcategoria("");
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {(rootCategories || []).map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.nome}
                </MenuItem>
              ))}
            </TextField>
          </CardContent></Card>
        </Grid>
        {showSubcategoryFilter && (
          <Grid item xs={12} md={4}>
            <Card><CardContent>
              <TextField
                select
                fullWidth
                size="small"
                label="Filtrar por subcategoria"
                value={idSubcategoria}
                onChange={(e) => setIdSubcategoria(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {subcategoryOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </CardContent></Card>
          </Grid>
        )}
        <Grid item xs={12} md={showSubcategoryFilter ? 4 : 6}>
          <Card><CardContent>
            <TextField
              select fullWidth size="small" label="Filtrar por disponibilidade"
              value={availabilityFilter} onChange={(e)=>setAvailabilityFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="imediata">Imediata</MenuItem>
              <MenuItem value="sob-encomenda">Sob encomenda</MenuItem>
            </TextField>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* ===== LINHA 2: KPIs ===== */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          {chartsLoading ? <Skeleton height={118} /> : (
            <Card sx={{ height:"100%" }}>
              <CardContent sx={{ display:"flex", alignItems:"center", gap:2 }}>
                <Avatar sx={{ bgcolor:p.primary.main, width:44, height:44 }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                      Faturamento Total
                    </Typography>
                    <AiBtn
                      cfg={{
                        metric: "total_revenue",
                        metricName: "Faturamento Total",
                        metricDefinition: "Receita total no período e filtros selecionados (impacta diretamente o resultado financeiro).",
                        metricValue: cards.totalRevenue || 0,
                        metricHuman: BRL(cards.totalRevenue || 0),
                      }}
                    />
                  </Stack>
                  <Typography variant="h4" sx={{ color: p.primary.main }}>
                    {BRL(cards.totalRevenue || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Período selecionado</Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {chartsLoading ? <Skeleton height={118} /> : (
            <Card sx={{ height:"100%" }}>
              <CardContent sx={{ display:"flex", alignItems:"center", gap:2 }}>
                <Avatar sx={{ bgcolor:(p.secondary?.main ?? p.info.main), width:44, height:44 }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                      Ticket Médio (un)
                    </Typography>
                    <AiBtn
                      cfg={{
                        metric: "avg_ticket_unit",
                        metricName: "Ticket Médio (un)",
                        metricDefinition: "Valor médio por unidade vendida (impacta margem e faturamento por volume).",
                        metricValue: cards.avgTicketUnit || 0,
                        metricHuman: BRL(cards.avgTicketUnit || 0),
                      }}
                    />
                  </Stack>
                  <Typography variant="h4" sx={{ color: (p.secondary?.main ?? p.info.main) }}>
                    {BRL(cards.avgTicketUnit || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(cards.totalSalesUnits||0).toLocaleString("pt-BR")} un vendidas
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {chartsLoading ? <Skeleton height={118} /> : (
            <Card sx={{ height:"100%" }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor:p.warning.main, width:44, height:44 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box sx={{ flex:1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                        Conversão Média
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "avg_conv_pct",
                          metricName: "Conversão Média",
                          metricDefinition: "Percentual médio de conversão (procuras/visitas que viram venda).",
                          metricValue: Number(cards.avgConvPct || 0),
                          metricHuman: `${Number(cards.avgConvPct||0).toFixed(1)}%`,
                        }}
                      />
                    </Stack>
                    <Typography variant="h4">
                      {Number(cards.avgConvPct||0).toFixed(1)}%
                    </Typography>
                  </Box>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Number(cards.avgConvPct||0))}
                  sx={{ mt:1, height:6, borderRadius:3 }}
                />
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {chartsLoading ? <Skeleton height={118} /> : (
            <Card sx={{ height:"100%" }}>
              <CardContent sx={{ display:"flex", alignItems:"center", gap:2 }}>
                <Avatar sx={{ bgcolor:p.error.main, width:44, height:44 }}>
                  <LocalOfferIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                      % SKUs em Promoção
                    </Typography>
                    <AiBtn
                      cfg={{
                        metric: "promo_pct",
                        metricName: "% SKUs em Promoção",
                        metricDefinition: "Percentual de SKUs com promoção ativa (impacta volume, margem e mix).",
                        metricValue: Number(cards.promoPct || 0),
                        metricHuman: `${Number(cards.promoPct||0).toFixed(1)}%`,
                      }}
                    />
                  </Stack>
                  <Typography variant="h4" sx={{ color: p.error.main }}>
                    {Number(cards.promoPct||0).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Catálogo filtrado</Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ===== Gráficos ===== */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{height:'100%'}}><CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Curva ABC — Receita por SKU</Typography>
              <AiBtn
                cfg={{
                  metric: "abc_curve",
                  metricName: "Curva ABC — Receita por SKU",
                  metricDefinition: "Distribuição de receita por SKU (concentração em poucos itens vs cauda longa).",
                  metricValue: charts?.abc || [],
                  metricHuman: "",
                }}
              />
            </Stack>
            {chartsLoading ? <Skeleton variant="rectangular" height={300} /> : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts?.abc || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tickFormatter={(t)=>t?.length>16?t.slice(0,16)+"…":t} />
                  <YAxis />
                  <RTooltip formatter={(v,n,p)=> n==="cumul" ? [`${v}%`,"Acumulado"] : [BRL(v), p?.payload?.label]} />
                  <Bar dataKey="revenue" name="Receita (R$)" fill={p.primary.main} />
                  <Line type="monotone" dataKey="cumul" name="Acumulado (%)" stroke={(p.secondary?.main ?? p.info.main)} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <Typography variant="caption" color="text.secondary">Classe A ≤ 80%, B ≤ 95%, C &gt; 95%</Typography>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{height:'100%'}}><CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Conversão por Categoria</Typography>
              <AiBtn
                cfg={{
                  metric: "conv_by_category",
                  metricName: "Conversão por Categoria",
                  metricDefinition: "Comparativo de conversão por categoria para encontrar gargalos e oportunidades de otimização.",
                  metricValue: charts?.convByCat || [],
                  metricHuman: "",
                }}
              />
            </Stack>
            {chartsLoading ? <Skeleton variant="rectangular" height={300} /> : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts?.convByCat || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" /><YAxis unit="%" />
                  <RTooltip formatter={(v)=>[`${v}%`,"Conversão"]} />
                  <Bar dataKey="value" name="Conversão (%)">
                    {(charts?.convByCat || []).map((d,i)=><Cell key={i} fill={SERIES[i%SERIES.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{height:'100%'}}><CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Oportunidades — Muito procurados e pouca venda</Typography>
              <AiBtn
                cfg={{
                  metric: "oportunidades_gap",
                  metricName: "Oportunidades — Muito procurados e pouca venda",
                  metricDefinition: "SKUs com alta procura e baixa venda (sinaliza ajustes em preço, conteúdo, estoque e oferta).",
                  metricValue: charts?.oportunidades || [],
                  metricHuman: "",
                }}
              />
            </Stack>
            {chartsLoading ? <Skeleton variant="rectangular" height={300} /> : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts?.oportunidades || []} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="label" width={180} tickFormatter={(t)=>t?.length>18?t.slice(0,18)+"…":t} />
                  <RTooltip formatter={(v)=>[v,"Gap Score"]} />
                  <Bar dataKey="gapScore" name="Gap Score" fill={p.warning.main} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <Typography variant="caption" color="text.secondary">
              Aja nesses SKUs: preço, descrição, fotos, frete/condições e estoque.
            </Typography>
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{height:'100%'}}><CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Mix de SKUs & Disponibilidade</Typography>
              <AiBtn
                cfg={{
                  metric: "mix_skus_disponibilidade",
                  metricName: "Mix de SKUs & Disponibilidade",
                  metricDefinition: "Distribuição do mix de SKUs por categoria e por disponibilidade (impacta conversão e nível de serviço).",
                  metricValue: { mixCat: charts?.mixCat || [], mixDisp: charts?.mixDisp || [] },
                  metricHuman: "",
                }}
              />
            </Stack>
            {chartsLoading ? <Skeleton variant="rectangular" height={260} /> : (
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ height: 260, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={charts?.mixCat || []}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={isSmDown ? 70 : 82}
                            label={false}
                          >
                            {(charts?.mixCat || []).map((d,i)=><Cell key={i} fill={SERIES[i%SERIES.length]} />)}
                          </Pie>
                        <RTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <LegendList
                      items={charts?.mixCat || []}
                      getColor={(i)=>SERIES[i%SERIES.length]}
                      maxHeight={isSmDown ? 92 : 84}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ height: 260, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={charts?.mixDisp || []}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={isSmDown ? 70 : 82}
                            label={false}
                          >
                            {(charts?.mixDisp || []).map((d,i)=><Cell key={i} fill={[p.success.main, p.warning.main][i%2]} />)}
                          </Pie>
                        <RTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <LegendList
                      items={charts?.mixDisp || []}
                      getColor={(i)=>[p.success.main, p.warning.main][i%2]}
                      maxHeight={isSmDown ? 72 : 64}
                    />
                  </Box>
                </Grid>
              </Grid>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card><CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Evolução Mensal — Receita, Quantidade e Conversão</Typography>
              <AiBtn
                cfg={{
                  metric: "monthly_evolution",
                  metricName: "Evolução Mensal — Receita, Quantidade e Conversão",
                  metricDefinition: "Tendência mensal de receita, volume e conversão para identificar sazonalidade e impacto de ações comerciais.",
                  metricValue: charts?.monthlyAgg || [],
                  metricHuman: "",
                }}
              />
            </Stack>
            {chartsLoading ? <Skeleton variant="rectangular" height={300} /> : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={charts?.monthlyAgg || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" unit="%" />
                  <RTooltip />
                  <Line yAxisId="left" type="monotone" dataKey="revenue"  name="Receita (R$)" stroke={p.primary.main} dot={false} />
                  <Line yAxisId="left" type="monotone" dataKey="quantity" name="Vendas (un)" stroke={p.warning.main} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="conv"     name="Conversão (%)" stroke={(p.secondary?.main ?? p.info.main)} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent></Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* ===== Tabela ===== */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Detalhamento de Produtos (vendas & conversão)
        </Typography>
        <AiBtn
          cfg={{
            metric: "products_table",
            metricName: "Detalhamento de Produtos (vendas & conversão)",
            metricDefinition: "Tabela detalhada por produto para priorizar melhorias no catálogo e maximizar conversão e receita.",
            metricValue: { sample: (rowsGrid || []).slice(0, 25) },
            metricHuman: "",
          }}
        />
      </Stack>

      <DataGrid
        autoHeight
        rows={rowsGrid}
        columns={columnsGrid}
        getRowId={(row) => row.id}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode="server"
        rowCount={totalRows}
        loading={tableLoading}
        page={page - 1}
        onPageChange={(newPage) => setPage(newPage + 1)}
        onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(1); }}
        density="comfortable"
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
          sorting: { sortModel: [{ field: "revenue", sort: "desc" }] },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": { bgcolor: theme.palette.mode === "dark" ? alpha("#fff",0.03) : "#F5F7FA" },
          "& .MuiDataGrid-cell": { alignItems: "center" },
        }}
      />

      {chartsError && (
        <Typography sx={{ mt: 2 }} color="error">
          Erro ao carregar dados: {String(chartsError?.message || chartsError)}
        </Typography>
      )}

      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start, end }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="produtos"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Box>
  );
};

export default Produtos;
